/**
 * Converts a string to Title Case.
 * Normalises ALL-CAPS locale DB values so every word is capitalised correctly.
 *
 * Rules:
 * - Words whose uppercase form is in PRESERVED_ACRONYMS are emitted in all-caps
 *   regardless of the original casing (e.g. "mrn" → "MRN", "MRN" → "MRN").
 * - All other words have their first character uppercased and the rest left
 *   as-is, so existing mixed-case or accented strings are not damaged.
 * - The word boundary pattern uses Unicode letter/number/combining-mark ranges
 *   instead of `\w` so that accented letters (é, ü, ñ …) are matched in
 *   supported locales (fr, de, tr, …).
 *
 * @example toTitleCase('MILK MANAGEMENT') // → 'Milk Management'
 * @example toTitleCase('patient mrn')     // → 'Patient MRN'
 * @example toTitleCase('patient MRN')     // → 'Patient MRN'
 * @example toTitleCase('IP address')      // → 'IP Address'
 * @example toTitleCase('item sku')        // → 'Item SKU'
 * @example toTitleCase('prénom')          // → 'Prénom'
 */
// Unicode "word" segment: letters (L), combining marks (M), digits (N), connectors (Pc)
const WORD_RE = /[\p{L}\p{M}\p{N}\p{Pc}]+/gu;

/**
 * Explicit allowlist of tokens that must never be re-cased.
 * Checked case-insensitively so "mrn", "Mrn", and "MRN" all resolve to "MRN".
 */
const PRESERVED_ACRONYMS = new Set([
  'ID',
  'IP',
  'MRN',
  'NICU',
  'SKU',
]);

export function toTitleCase(str: string): string {
  return str.replace(WORD_RE, (word) => {
    if (PRESERVED_ACRONYMS.has(word.toUpperCase())) {
      // Return the canonical upper-case form so "mrn" → "MRN" as well.
      return word.toUpperCase();
    }
    // Capitalise first code-point; leave the rest unchanged so mixed-case
    // and accented words are not accidentally lowercased.
    const first = [...word][0];
    return first.toUpperCase() + word.slice(first.length);
  });
}
